import type { Language, Role, ProviderDraft } from '@harmony/shared';

export interface Me {
  id: string;
  telegramUserId: string;
  telegramUsername: string | null;
  firstName: string | null;
  displayName: string | null;
  role: Role | null;
  language: Language;
  languageChosen: boolean;
  referralCode: string;
}

export interface City {
  id: string;
  nameEn: string;
  nameAm: string;
  areas: Array<{ id: string; nameEn: string; nameAm: string }>;
}

export interface ClientProfile {
  displayName: string;
  gender: 'MALE' | 'FEMALE';
  phone: string;
  cityId: string | null;
  hasGps: boolean;
  city: { nameEn: string; nameAm: string } | null;
}

export type Draft = ProviderDraft & { agentVia?: 'CODE' | 'LINK' };

export interface ProviderRegistration {
  status: 'NONE' | 'DRAFT' | 'PENDING' | 'APPROVED' | 'DENIED';
  denialReason: string | null;
  draft: Draft;
  previews: { photos: Array<{ key: string; url: string }>; idPhoto?: string; selfie?: string };
  agent: { name: string; agentCode: string } | null;
  agentLocked: boolean;
  sessionTypes: Array<{ id: string; minutes: number; nameEn: string; nameAm: string }>;
}

export interface AgentMe {
  name: string;
  agentCode: string;
  providerCount: number;
  inviteLink: string;
}

export interface Named {
  nameEn: string;
  nameAm: string;
}

export interface ProviderCard {
  id: string;
  displayName: string;
  gender: 'MALE' | 'FEMALE';
  age: number;
  bio: string | null;
  city: Named | null;
  area: Named | null;
  available: boolean;
  availableSince: string | null;
  ratingAvg: number | null;
  ratingCount: number;
  distanceKm: number | null;
  photos: string[];
  prices: Array<{ minutes: number; price: number }>;
  isFavorite: boolean;
}

export interface DiscoverResult {
  mode: 'nearby' | 'city' | 'all';
  total: number;
  items: ProviderCard[];
  nextOffset: number | null;
}

export interface DiscoverFilters {
  radiusKm?: number;
  gender?: 'MALE' | 'FEMALE';
  minutes?: number;
  maxPrice?: number;
  minRating?: number;
  availableOnly?: boolean;
  offset?: number;
  limit?: number;
}

export interface Quota {
  free: number;
  paid: number;
  total: number;
  quota: number;
  intervalHours: number;
  nextRechargeAt: string;
  expiryMinutes: number;
}

export type BookingStatus =
  | 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED' | 'CANCELLED_BY_CLIENT'
  | 'CANCELLED_BY_PROVIDER' | 'COMPLETED' | 'DISPUTED' | 'CLOSED';

export interface Booking {
  id: string;
  status: BookingStatus;
  minutes: number;
  price: number;
  requestedAt: string;
  expiresAt: string;
  acceptedAt: string | null;
  conversationId: string | null;
  provider?: { id: string; name: string; photo: string | null };
  client?: { id: string; name: string; gender: 'MALE' | 'FEMALE' | null; city: Named | null; area: Named | null };
}

export type MessageType = 'TEXT' | 'PHOTO' | 'VOICE' | 'VIDEO';

export interface ChatMessage {
  id: string;
  seq: string;
  clientMsgId: string;
  type: MessageType;
  text: string | null;
  mediaUrl: string | null;
  durationSec: number | null;
  mine: boolean;
  createdAt: string;
  readAt: string | null;
}

export interface Conversation {
  id: string;
  bookingId: string;
  canSend: boolean;
  other: { id: string; name: string; photo: string | null };
  lastMessage: { type: MessageType; text: string | null; mine: boolean; at: string } | null;
  activityAt: string;
  unread: number;
}

export interface EditSuggestion {
  displayName?: string;
  bio?: string;
  prices?: Record<string, number>;
}

export interface ProviderEditForm {
  displayName: string;
  bio: string;
  phone: string;
  gender: 'MALE' | 'FEMALE';
  age: number;
  cityId: string | null;
  hasGps: boolean;
  photos: Array<{ key: string; url: string }>;
  prices: Record<string, number>;
  sessionLengths: number[];
}

export interface MyProviderProfile {
  approvalStatus: 'DRAFT' | 'PENDING' | 'APPROVED' | 'DENIED';
  available: boolean;
  isActive: boolean;
  inactiveBy: 'AGENT' | 'PROVIDER' | 'ADMIN' | null;
  agent: { name: string; agentCode: string } | null;
  card: ProviderCard | null;
  suggestions: Array<{
    id: string;
    agentName: string;
    createdAt: string;
    changes: EditSuggestion;
    current: { displayName: string; bio: string; prices: Record<string, number> };
  }>;
}

export type LedgerType = 'CREDIT_PURCHASE' | 'CREDIT_SPEND' | 'CREDIT_RECHARGE' | 'REFERRAL_REWARD' | 'ADMIN_ADJUSTMENT' | 'REFUND';

export interface CreditPackage {
  id: string;
  nameEn: string;
  nameAm: string;
  credits: number;
  price: number;
}

export interface Payment {
  id: string;
  purpose: 'CREDIT_PACKAGE' | 'AGENT_SUBSCRIPTION';
  status: string;
  amount: number;
  discount: number;
  credits: number | null;
  packageName: { en: string; am: string } | null;
  coupon: string | null;
  createdAt: string;
  reason: string | null;
}

export interface WalletView {
  quota: Quota;
  history: Array<{ id: string; type: LedgerType; credits: number; amount: number; createdAt: string }>;
  payments: Payment[];
  packages: CreditPackage[];
  canBuy: boolean;
  couponsEnabled: boolean;
  instructions: string;
}

export interface Referrals {
  enabled: boolean;
  link: string;
  joined: number;
  rewarded: number;
  creditsEarned: number;
  rewardCredits: number;
  event: 'REGISTERED' | 'FIRST_BOOKING';
}

export interface Promotion {
  id: string;
  code: string;
  bannerEn: string;
  bannerAm: string;
  endAt: string;
}

export interface OnboardingItem {
  id: string;
  title: string;
  body: string | null;
  done: boolean;
}

export interface FeedbackPrompt {
  id: string;
  bookingId: string;
  minutes: number;
  provider: { id: string; name: string; photo: string | null };
}

export type AgentPeriod = 'today' | 'week' | 'month' | 'last_month';
export type AgentProviderStatus = 'active' | 'inactive' | 'pending' | 'denied' | 'draft';

export interface AgentProvider {
  id: string;
  name: string;
  photo: string | null;
  status: AgentProviderStatus;
  inactiveBy: string | null;
  available: boolean;
  ratingAvg: number | null;
  ratingCount: number;
  linkedAt: string;
  requestsThisMonth: number;
  acceptedThisMonth: number;
  editPending: boolean;
}

export interface AgentProviderDetail extends AgentProvider {
  card: ProviderCard | null;
  pendingSuggestion: { id: string; changes: EditSuggestion; created_at: string } | null;
  month: { received: number; accepted: number; declined: number; expired: number; completed: number };
}

export interface AgentStats {
  period: AgentPeriod;
  activeProviders: number;
  newProviders: number;
  received: number;
  accepted: number;
  declined: number;
  cancelled: number;
  completed: number;
  expired: number;
}

export interface AgentRequest {
  id: string;
  status: BookingStatus;
  requestedAt: string;
  minutes: number;
  price: number;
  provider: { id: string; name: string };
  city: Named | null;
  area: Named | null;
}

export interface AgentSubscription {
  enabled: boolean;
  providerCount: number;
  pricePerProvider: number;
  amount: number;
  periodDays: number;
  paidUntil: string | null;
  due: boolean;
  waiting: boolean;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    public issues: Array<{ path: string; message: string }> = [],
    public body: Record<string, unknown> = {},
  ) {
    super(code);
  }
}

export function getToken() {
  return token;
}

/** A random id for one user action, so retries never do it twice. */
export function actionId() {
  return (crypto.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/[^a-zA-Z0-9-]/g, '');
}

function qs(params: Record<string, unknown>) {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) if (v !== undefined && v !== null && v !== '') p.set(k, String(v));
  const s = p.toString();
  return s ? `?${s}` : '';
}

const BASE = import.meta.env.VITE_API_URL ?? '/api';
let token: string | null = null;

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const isForm = init.body instanceof FormData;
  const res = await fetch(BASE + path, {
    ...init,
    headers: {
      ...(isForm ? {} : { 'content-type': 'application/json' }),
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  const body = await res.json().catch(() => null);
  if (!res.ok) {
    const code = body?.code ?? body?.message?.code ?? 'REQUEST_FAILED';
    throw new ApiError(res.status, code, body?.issues ?? [], body ?? {});
  }
  return body as T;
}

const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) });

export const api = {
  async login(initData: string, startParam?: string) {
    const r = await request<{ token: string; user: Me; agentCode: string | null }>('/auth/telegram', json('POST', { initData, startParam }));
    token = r.token;
    return r;
  },
  me: () => request<Me>('/me'),
  setLanguage: (language: Language) => request<Me>('/me/language', json('PATCH', { language })),
  chooseRole: (role: 'CLIENT' | 'PROVIDER') => request<Me>('/me/role', json('POST', { role })),
  publicSettings: () => request<Record<string, unknown>>('/settings/public'),
  cities: () => request<City[]>('/geo/cities'),

  clientProfile: () => request<ClientProfile>('/clients/me'),
  createClient: (body: unknown) => request<ClientProfile>('/clients/me', json('POST', body)),

  providerRegistration: () => request<ProviderRegistration>('/providers/me/registration'),
  saveDraft: (draft: Partial<Draft>) => request<ProviderRegistration>('/providers/me/draft', json('PUT', draft)),
  submitProvider: () => request<ProviderRegistration>('/providers/me/submit', { method: 'POST' }),
  agentLookup: (code: string) => request<{ name: string; agentCode: string }>(`/agents/lookup?code=${encodeURIComponent(code)}`),
  agentMe: () => request<AgentMe>('/agents/me'),

  upload(purpose: 'provider_photo' | 'id_photo' | 'id_selfie' | 'chat_photo' | 'chat_voice' | 'chat_video' | 'payment_evidence', file: Blob, name = 'file') {
    const fd = new FormData();
    fd.append('file', file, name);
    return request<{ key: string; url: string; mime?: string }>(`/uploads?purpose=${purpose}`, { method: 'POST', body: fd });
  },
  updateClientLocation: (location: { lat?: number; lng?: number; cityId?: string }) =>
    request<ClientProfile>('/clients/me', json('PATCH', { location })),

  // Discovery
  providers: (f: DiscoverFilters = {}) => request<DiscoverResult>(`/providers${qs({ ...f })}`),
  provider: (id: string) => request<ProviderCard>(`/providers/${id}`),
  favorites: () => request<ProviderCard[]>('/favorites'),
  addFavorite: (id: string) => request<{ isFavorite: boolean }>(`/favorites/${id}`, { method: 'POST' }),
  removeFavorite: (id: string) => request<{ isFavorite: boolean }>(`/favorites/${id}`, { method: 'DELETE' }),
  blocks: () => request<Array<{ id: string; displayName: string }>>('/blocks'),
  block: (id: string) => request<{ ok: true }>(`/blocks/${id}`, json('POST', {})),
  unblock: (id: string) => request<{ ok: true }>(`/blocks/${id}`, { method: 'DELETE' }),
  report: (providerId: string, category: string, details?: string) =>
    request<{ ok: true }>('/reports', json('POST', { providerId, category, details: details || undefined })),
  myProviderProfile: () => request<MyProviderProfile>('/providers/me/profile'),
  setAvailability: (available: boolean) => request<{ available: boolean }>('/providers/me/availability', json('PATCH', { available })),

  // Bookings
  quota: () => request<Quota>('/bookings/quota'),
  book: (providerId: string, minutes: number, idempotencyKey: string) =>
    request<{ booking: Booking; quota: Quota }>('/bookings', json('POST', { providerId, minutes, idempotencyKey })),
  myBookings: () => request<Booking[]>('/bookings/mine'),
  incoming: () => request<Booking[]>('/bookings/incoming'),
  accept: (id: string) => request<{ conversationId: string }>(`/bookings/${id}/accept`, { method: 'POST' }),
  decline: (id: string) => request<{ ok: true }>(`/bookings/${id}/decline`, { method: 'POST' }),
  cancelBooking: (id: string, reason: string) => request<{ ok: true; refunded: boolean }>(`/bookings/${id}/cancel`, json('POST', { reason })),

  // Chat
  conversations: () => request<Conversation[]>('/conversations'),
  unread: () => request<{ unread: number }>('/conversations/unread'),
  messages: (id: string, before?: string) =>
    request<{ conversation: { id: string; canSend: boolean; otherName: string }; messages: ChatMessage[]; hasMore: boolean }>(
      `/conversations/${id}/messages${qs({ before })}`,
    ),
  send: (id: string, body: { clientMsgId: string; type: MessageType; text?: string; mediaKey?: string; durationSec?: number }) =>
    request<ChatMessage>(`/conversations/${id}/messages`, json('POST', body)),
  markRead: (id: string) => request<{ ok: true }>(`/conversations/${id}/read`, { method: 'POST' }),

  // Wallet & payments
  wallet: () => request<WalletView>('/wallet'),
  checkCoupon: (code: string, packageId: string) => request<{ price: number; discount: number; total: number }>('/wallet/coupon', json('POST', { code, packageId })),
  pay: (body: { purpose?: 'CREDIT_PACKAGE' | 'AGENT_SUBSCRIPTION'; packageId?: string; couponCode?: string; evidenceKey?: string; idempotencyKey: string }) =>
    request<Payment>('/payments', json('POST', body)),
  paymentInstructions: () => request<{ text: string }>('/payments/instructions'),

  // Growth
  referrals: () => request<Referrals>('/referrals/me'),
  promotions: () => request<Promotion[]>('/promotions/active'),
  onboarding: () => request<{ tutorial: OnboardingItem[]; checklist: OnboardingItem[] }>('/onboarding'),
  tutorialSeen: () => request<{ ok: true }>('/onboarding/tutorial-seen', { method: 'POST' }),
  help: () => request<{ faq: string; announcement: string; supportUsername: string | null; botLink: string }>('/help'),
  feedbackPending: () => request<FeedbackPrompt[]>('/feedback/pending'),
  answerFeedback: (id: string, body: { outcome: 'MET'; rating: number; comment?: string } | { outcome: 'NOT_MET_YET' } | { outcome: 'CANCELED'; reason: string }) =>
    request<{ ok: true; outcome: string; askAgainAt: string | null }>(`/feedback/${id}`, json('POST', body)),

  // Provider self-management
  providerEditForm: () => request<ProviderEditForm>('/providers/me/edit'),
  saveProvider: (body: { displayName?: string; bio?: string; phone?: string; prices?: Record<string, number>; photoKeys?: string[]; location?: { lat?: number; lng?: number; cityId?: string } }) =>
    request<ProviderEditForm>('/providers/me/profile', json('PATCH', body)),
  saveClient: (body: { displayName?: string; gender?: 'MALE' | 'FEMALE'; phone?: string; location?: { lat?: number; lng?: number; cityId?: string } }) =>
    request<ClientProfile>('/clients/me', json('PATCH', body)),
  setActive: (active: boolean) => request<{ ok: true }>('/providers/me/active', json('PATCH', { active })),
  answerSuggestion: (id: string, answer: 'accept' | 'reject') => request<{ ok: true }>(`/providers/me/suggestions/${id}/${answer}`, { method: 'POST' }),

  // Agents
  agentProviders: () => request<AgentProvider[]>('/agents/me/providers'),
  agentProvider: (id: string) => request<AgentProviderDetail>(`/agents/me/providers/${id}`),
  agentSetActive: (id: string, active: boolean) => request<{ ok: true }>(`/agents/me/providers/${id}/active`, json('PATCH', { active })),
  agentSuggest: (id: string, changes: EditSuggestion) => request<{ ok: true }>(`/agents/me/providers/${id}/suggestions`, json('POST', changes)),
  agentRemove: (id: string) => request<{ ok: true }>(`/agents/me/providers/${id}`, { method: 'DELETE' }),
  agentStats: (period: AgentPeriod) => request<AgentStats>(`/agents/me/stats?period=${period}`),
  agentRequests: (period: AgentPeriod) => request<AgentRequest[]>(`/agents/me/requests?period=${period}`),
  agentSubscription: () => request<AgentSubscription>('/agents/me/subscription'),
  agentLinkShared: () => request<{ ok: true }>('/agents/me/link-shared', { method: 'POST' }),
};
